import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuFolder,
  LuBookOpen,
  LuSearch,
  LuCheck,
  LuSave,
} from "react-icons/lu";
import { getCategories } from "../../../../../api/categories";
import {
  updateCatalogsToProduct,
  updateCategoriesToProduct,
} from "../../../../../api/products";
import { getCatalogs } from "../../../../../api/catalogs";
import { updateFloatPanelData } from "../../../../../reducers/userConfigSlice";
import "./organization.css";

export const MembershipPicker = ({
  title,
  description,
  idKey,
  productId,
  initial = [],
  load,
  save,
  onSaved,
  icon,
  compact = false,
}) => {
  const [options, setOptions] = useState([]);
  const [expanded, setExpanded] = useState(!compact);
  const [selected, setSelected] = useState(initial.map((row) => row[idKey]));
  const [saved, setSaved] = useState(initial.map((row) => row[idKey]));
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const [retry, setRetry] = useState(0);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const initialKey = initial
    .map((row) => row[idKey])
    .sort()
    .join(",");
  useEffect(() => {
    const ids = initialKey ? initialKey.split(",") : [];
    setSelected(ids);
    setSaved(ids);
  }, [productId, initialKey]);
  useEffect(() => {
    setMessage("");
  }, [productId]);
  useEffect(() => {
    let active = true;
    alive.current = true;
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => load())
      .then((response) => {
        if (!active) return;
        if (response?.error !== false || !Array.isArray(response?.data))
          throw new Error("unavailable");
        setOptions(response.data);
      })
      .catch(() => {
        if (active) setError(`No hemos podido cargar ${title.toLowerCase()}.`);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      alive.current = false;
    };
  }, [load, productId, retry, title]);
  const dirty = [...selected].sort().join(",") !== [...saved].sort().join(",");
  const toggle = (id) => {
    setSelected((ids) =>
      ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id],
    );
    setMessage("");
  };
  const submit = async () => {
    if (inFlight.current || !dirty) return;
    inFlight.current = true;
    setPending(true);
    setError("");
    setMessage("");
    try {
      const response = await save(productId, selected);
      if (response?.error !== false) throw new Error("save failed");
      if (!alive.current) return;
      setSaved([...selected]);
      if (compact) setExpanded(false);
      onSaved(options.filter((row) => selected.includes(row[idKey])));
      setMessage("Cambios guardados");
    } catch {
      if (alive.current)
        setError(
          "No hemos podido guardar. Tus cambios siguen aquí; vuelve a intentarlo.",
        );
    } finally {
      inFlight.current = false;
      if (alive.current) setPending(false);
    }
  };
  const visible = options.filter(
    (row) =>
      (row.isActive || selected.includes(row[idKey])) &&
      `${row.name || ""} ${row.description || ""}`
        .toLocaleLowerCase("es")
        .includes(search.toLocaleLowerCase("es")),
  );
  return (
    <section
      className="product-membership"
      aria-label={title}
      aria-busy={pending || loading}
      data-editor-write-pending={pending}
    >
      <div className="membership-heading">
        <span className="membership-icon" aria-hidden="true">
          {icon}
        </span>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <span className="membership-count">
          {selected.length}{" "}
          {selected.length === 1 ? "seleccionado" : "seleccionados"}
        </span>
      </div>
      {compact && (
        <>
          <div className="membership-selected-list">
            {options
              .filter((row) => selected.includes(row[idKey]))
              .map((row) => (
                <div className="membership-selected-row" key={row[idKey]}>
                  <span>
                    <strong>{row.name}</strong>
                    {row.description && <small>{row.description}</small>}
                    {!row.isActive && <small>Inactivo</small>}
                  </span>
                  <button
                    type="button"
                    disabled={pending}
                    aria-label={`Quitar ${row.name} de la selección`}
                    onClick={() => toggle(row[idKey])}
                  >
                    ×
                  </button>
                </div>
              ))}
          </div>
          <button
            type="button"
            className="customButton membership-add"
            aria-expanded={expanded}
            disabled={pending || loading}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Cerrar selección" : `Agregar ${title.toLowerCase()}`}
          </button>
        </>
      )}
      {loading ? (
        <p role="status">Cargando {title.toLowerCase()}…</p>
      ) : (
        <>
          <div hidden={!expanded}>
            {options.length > 4 && (
              <label className="membership-search">
                <LuSearch aria-hidden="true" />
                <span className="visually-hidden">
                  Buscar {title.toLowerCase()}
                </span>
                <input
                  type="search"
                  value={search}
                  disabled={pending}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={`Buscar ${title.toLowerCase()}`}
                />
              </label>
            )}
            <fieldset disabled={pending} className="membership-options">
              <legend className="visually-hidden">
                Incluir producto en {title.toLowerCase()}
              </legend>
              {visible.map((row) => (
                <label
                  key={row[idKey]}
                  className={`membership-option ${selected.includes(row[idKey]) ? "is-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(row[idKey])}
                    onChange={() => toggle(row[idKey])}
                  />
                  <span className="membership-option-copy">
                    <strong>{row.name}</strong>
                    {row.description && <small>{row.description}</small>}
                    {!row.isActive && <small>Inactivo</small>}
                    {row.isDefault && (
                      <small>
                        {idKey === "catalogId"
                          ? "Catálogo principal"
                          : "Categoría principal"}
                      </small>
                    )}
                  </span>
                  <span className="membership-state" aria-hidden="true">
                    {selected.includes(row[idKey]) ? (
                      <>
                        <LuCheck /> Incluido
                      </>
                    ) : (
                      "Incluir"
                    )}
                  </span>
                </label>
              ))}
            </fieldset>
          </div>
          {!visible.length && !error && (
            <p className="membership-empty">
              {search
                ? "No hay coincidencias con esa búsqueda."
                : `Todavía no hay ${title.toLowerCase()} disponibles.`}
            </p>
          )}
        </>
      )}
      {error && (
        <div className="membership-error" role="alert">
          {error}
          {!options.length && !loading && (
            <button
              type="button"
              onClick={() => setRetry((value) => value + 1)}
            >
              Volver a cargar
            </button>
          )}
        </div>
      )}
      <div className="membership-footer">
        <span role="status">
          {message ||
            (dirty
              ? "Tienes cambios sin guardar"
              : "Selecciona dónde quieres incluir este producto.")}
        </span>
        <button
          type="button"
          className="membership-save"
          disabled={!dirty || pending || loading || !options.length}
          onClick={submit}
        >
          <LuSave aria-hidden="true" />
          {pending ? "Guardando…" : `Guardar ${title.toLowerCase()}`}
        </button>
      </div>
    </section>
  );
};

export const ProductOrganization = ({ compact = false }) => {
  const dispatch = useDispatch();
  const data = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.data,
  );
  return (
    <div className="product-memberships">
      <MembershipPicker
        compact={compact}
        title="Categorías"
        description="Agrupa el producto para que sea fácil encontrarlo en la carta."
        idKey="categoryId"
        productId={data?.productId}
        initial={data?.Categories}
        load={getCategories}
        save={updateCategoriesToProduct}
        onSaved={(rows) =>
          dispatch(updateFloatPanelData({ name: "Categories", data: rows }))
        }
        icon={<LuFolder />}
      />
      <MembershipPicker
        compact={compact}
        title="Catálogos"
        description="Elige las cartas en las que quieres incluir este producto."
        idKey="catalogId"
        productId={data?.productId}
        initial={data?.Catalogs}
        load={getCatalogs}
        save={updateCatalogsToProduct}
        onSaved={(rows) =>
          dispatch(updateFloatPanelData({ name: "Catalogs", data: rows }))
        }
        icon={<LuBookOpen />}
      />
    </div>
  );
};
